import * as React from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import "@geoman-io/leaflet-geoman-free";
import { LatLon } from "models/fenceSegment";

type Props = {
  isDrawingModeEnabled: boolean;
  isMobile: boolean;
  onPolylineComplete: (coords: LatLon[]) => void;
};

export function calculateSegmentFeet(vertices: L.LatLng[], mouseLatLng: L.LatLng | null): number {
  const points =
    mouseLatLng !== null && vertices.length > 0 ? [...vertices, mouseLatLng] : vertices;
  let totalMeters = 0;
  for (let i = 1; i < points.length; i++) {
    totalMeters += points[i - 1].distanceTo(points[i]);
  }
  return Math.ceil(totalMeters / 0.3048);
}

export function LeafletDrawingManager({ isDrawingModeEnabled, isMobile, onPolylineComplete }: Props) {
  const map = useMap();
  const tooltipRef = React.useRef<HTMLDivElement>(null);
  const workingLayerRef = React.useRef<L.Polyline | null>(null);

  React.useEffect(() => {
    map.pm.addControls({ drawControls: false, editControls: false });
    map.pm.setLang('en', {
      tooltips: {
        firstVertex: 'Click to place first fence post',
        continueLine: 'Click to add a bend, double-click to finish',
        finishLine: 'Double-click to finish the segment',
      },
    });
    return () => {
      map.pm.removeControls();
    };
  }, [map]);

  React.useEffect(() => {
    if (isDrawingModeEnabled) {
      map.pm.enableDraw("Line", { snappable: false, continueDrawing: false });
    } else {
      map.pm.disableDraw();
    }
    return () => {
      map.pm.disableDraw();
    };
  }, [map, isDrawingModeEnabled]);

  React.useEffect(() => {
    const handleCreate = (e: { layer: L.Layer; shape: string }) => {
      const polyline = e.layer as L.Polyline;
      const latLngs = polyline.getLatLngs() as L.LatLng[];
      const coords: LatLon[] = latLngs.map((ll) => ({ lat: ll.lat, lon: ll.lng }));
      e.layer.remove();
      onPolylineComplete(coords);
    };
    map.on("pm:create", handleCreate as unknown as L.LeafletEventHandlerFn);
    return () => {
      map.off("pm:create", handleCreate as unknown as L.LeafletEventHandlerFn);
    };
  }, [map, onPolylineComplete]);

  React.useEffect(() => {
    if (isMobile) return;

    const handleDrawStart = (e: { workingLayer: L.Layer }) => {
      workingLayerRef.current = e.workingLayer as L.Polyline;
      if (tooltipRef.current) tooltipRef.current.style.display = "block";
    };

    const handleMouseMove = (e: L.LeafletMouseEvent) => {
      if (!tooltipRef.current || !workingLayerRef.current) return;
      const vertices = workingLayerRef.current.getLatLngs() as L.LatLng[];
      const feet = calculateSegmentFeet(vertices, e.latlng);
      tooltipRef.current.textContent = `${feet} ft`;
      tooltipRef.current.style.left = `${e.containerPoint.x + 15}px`;
      tooltipRef.current.style.top = `${e.containerPoint.y - 30}px`;
    };

    const handleDrawEnd = () => {
      workingLayerRef.current = null;
      if (tooltipRef.current) tooltipRef.current.style.display = "none";
    };

    map.on("pm:drawstart", handleDrawStart as unknown as L.LeafletEventHandlerFn);
    map.on("mousemove", handleMouseMove as unknown as L.LeafletEventHandlerFn);
    map.on("pm:drawend", handleDrawEnd as unknown as L.LeafletEventHandlerFn);

    return () => {
      map.off("pm:drawstart", handleDrawStart as unknown as L.LeafletEventHandlerFn);
      map.off("mousemove", handleMouseMove as unknown as L.LeafletEventHandlerFn);
      map.off("pm:drawend", handleDrawEnd as unknown as L.LeafletEventHandlerFn);
    };
  }, [map, isMobile]);

  return (
    <div
      ref={tooltipRef}
      style={{
        display: "none",
        position: "absolute",
        zIndex: 9999,
        background: "rgba(0,0,0,0.75)",
        color: "white",
        borderRadius: "4px",
        padding: "3px 7px",
        fontSize: "12px",
        pointerEvents: "none",
        whiteSpace: "nowrap",
      }}
    />
  );
}
